import React, { ReactNode } from "react"
import { useTranslation } from "react-i18next"
import { Platform, ViewStyle } from "react-native"
import Animated, {
  useAnimatedStyle,
  interpolate,
} from "react-native-reanimated"
import { useSafeAreaInsets } from "react-native-safe-area-context"

import styled from "styled-components/native"

import { colorWithAlpha } from "@treefort/lib/color"
import rawTokens from "@treefort/tokens/app"

import useAppManifest from "../hooks/use-app-manifest"
import { useLockBodyScroll } from "../hooks/use-lock-body-scroll"
import { useOpenAnimation } from "../hooks/use-open-animation"
import { createStyle } from "../lib/create-style"
import { kebabCase } from "../lib/kebab-case"
import Box from "./box"
import { Heading } from "./heading"
import IconButton from "./icon-button"
import { KeyboardAvoidingView } from "./keyboard-controller"
import { Portal } from "./portal"
import Row from "./row"
import { SystemBars } from "./system-bars"
import TokensProvider, {
  DisplayMode,
  ResolvedTokens,
  useTokens,
} from "./tokens-provider"
import Touchable from "./touchable"

type BackgroundColor = keyof typeof rawTokens.colors.background

type AlignmentHorizontal = "left" | "center" | "right"

type AlignmentVertical = "top" | "center" | "bottom"

export type ModalType = "floating" | "fullscreen" | "sheet"

export const MODAL_HEADER_HEIGHT_PX = 46

const MODAL_ANIMATION_VERTICAL_PX = 64

const flexAlignment = {
  top: "flex-start",
  center: "center",
  bottom: "flex-end",
  left: "flex-start",
  right: "flex-end",
} as const

function getBorderRadiusStyles(modalType: ModalType, theme: ResolvedTokens) {
  switch (modalType) {
    case "floating":
      return `border-radius: ${theme.borderRadius.roundedLarge}px`
    case "sheet":
      return `
        border-top-right-radius: ${theme.borderRadius.roundedLarge}px;
        border-top-left-radius: ${theme.borderRadius.roundedLarge}px;
        border-bottom-right-radius: 0;
        border-bottom-left-radius: 0;
      `
    default:
      return `border-radius: 0`
  }
}

/**
 * Fills the screen and provides a canvas for the modal.
 */
const KeyboardAvoidingContainer = styled(KeyboardAvoidingView)`
  position: ${Platform.OS === "web" ? "fixed" : "absolute"};
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  overflow: hidden;
`

/**
 * Fills the screen and provides a canvas for the modal.
 */
const Container = styled(Animated.View)<{
  $backgroundColor?: BackgroundColor
  $touchable?: boolean
}>`
  background-color: ${(props) =>
    props.$backgroundColor
      ? props.theme.colors.background[props.$backgroundColor]
      : undefined};
  pointer-events: ${(props) => (props.$touchable ? "auto" : "box-none")};
  width: 100%;
  height: 100%;
`

/**
 * Renders the semi-opaque overlay behind the modal.
 */
const Scrim = styled.View<{ $show: boolean }>`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-color: ${(props) =>
    props.$show ? props.theme.colors.background.scrim : "transparent"};
  pointer-events: box-none;
`

/**
 * Used to ensure proper spacing between the modal and the edges of the screen.
 */
const Buffer = styled(Box).attrs({ width: "100%", height: "100%" })`
  pointer-events: box-none;
`

/**
 * Use a container for the box shadow since shadows are clipped by `overflow:
 * hidden` on iOS
 */
const BodyContainer = styled(Animated.View)<{
  $type: ModalType
  $maxWidth?: string | number
  $showScrim?: boolean
  $openState: "open" | "opening" | "closing"
  $backgroundColor: BackgroundColor
}>`
  background-color: ${(props) =>
    // Not specifying a background color on elements with a shadow generates a
    // warning on iOS
    props.theme.colors.background[props.$backgroundColor]};
  ${(props) => getBorderRadiusStyles(props.$type, props.theme)};
  max-width: ${(props) =>
    props.$type === "floating"
      ? typeof props.$maxWidth === "number"
        ? `${props.$maxWidth}px`
        : props.$maxWidth
      : "100%"};
  ${(props) =>
    // Box shadows are not necessary when there's a scrim
    props.$showScrim
      ? ""
      : // TODO: Drop the Android shadow when we upgrade to RN's "new
        // architecture": https://reactnative.dev/docs/view-style-props#boxshadow
        //
        // On Android we have to use the elevation API and we can't show shadows
        // during close animations without weird artifacting
        Platform.OS === "android" &&
          (props.$openState === "open" || props.$openState === "opening")
        ? `elevation: 22; shadow-color: ${props.theme.colors.black};`
        : // On iOS and the web we can use regular ole box-shadows.
          Platform.OS !== "android"
          ? `box-shadow: 0 4px 22px ${colorWithAlpha(props.theme.colors.black, 0.13)};`
          : ""}
  position: relative;
  width: 100%;
  /* Necessary to keep the body from overflowing vertically on the web */
  max-height: 100%;
`

/**
 * Renders the actual visual bounds of the modal including border, border
 * radius, background color, etc. (what you think of as the modal proper when
 * you look at the screen).
 */
const Body = styled.View<{
  $showScrim: boolean
  $type: ModalType
  $paddingBottom: number
  $paddingLeft: number
  $paddingRight: number
  $backgroundColor: BackgroundColor
}>`
  overflow: hidden;
  background-color: ${(props) =>
    props.theme.colors.background[props.$backgroundColor]};
  ${(props) => getBorderRadiusStyles(props.$type, props.theme)};
  border-style: solid;
  border-color: ${(props) => props.theme.colors.border.shadowAdjacent};
  ${(props) =>
    props.$type === "floating" && !props.$showScrim
      ? "border-width: 1px"
      : props.$type === "sheet" && !props.$showScrim
        ? "border-top-width: 1px"
        : ""};
  padding-bottom: ${(props) => props.$paddingBottom}px;
  padding-left: ${(props) => props.$paddingLeft}px;
  padding-right: ${(props) => props.$paddingRight}px;
  position: relative;
  width: 100%;
  /* Necessary to keep the body from overflowing vertically on the web */
  max-height: 100%;
`

const CloseButtonContainer = styled.View`
  position: absolute;
  right: 0;
  height: ${MODAL_HEADER_HEIGHT_PX}px;
  width: ${(props) => props.theme.minTapTarget}px;
  display: flex;
  align-items: center;
  justify-content: center;
`

const Title = styled(Heading)`
  flex: 1;
  padding-right: ${(props) => props.theme.minTapTarget}px;
`

const scrimTouchableStyle = createStyle({
  width: "100%",
  height: "100%",
})

/**
 * Render content in a modal over the rest of the app.
 *
 * If the modal should be closeable by the user at any time (most use-cases)
 * then set the open prop to false when onPressOutside and onPressCloseButton
 * are called. If the modal doesn't have a header then include some sort of
 * close button in the modal's children (e.g. a "Done" button) instead of
 * setting onPressCloseButton.
 *
 * The modal will expand vertically to fit the children passed to it until it
 * fills 100% of the screen height (minus modal margin + safe area insets).
 * Beyond this point the children will be clipped. If your modal has dynamic
 * content that might be clipped, wrap it in a ScrollView, FlatList,
 * SectionList, VirtualizedList, etc. Note that the modal makes use of the
 * useLockBodyScroll hook, so a data-web-scroll-unlock attribute should be set
 * on the scrollable element (this can be done by setting the `dataSet` prop to
 * `{{ webScrollUnlock: true }}`.
 *
 * The modal body does not include any built-in padding. In most cases you'll
 * want to apply "large" padding around your content. This padding should be
 * applied inside any scrollable view to ensure scrollbars are positioned at the
 * very edge of the modal.
 *
 * Note that for fullscreen modals no safe area insets are applied. This results
 * in a true fullscreen effect on native mobile, but requires that the content
 * placed inside the modal take safe area insets into account IF the modal is
 * displayed in fullscreen mode. This gets a bit tricky because the modal will
 * automatically switch out of fullscreen mode on larger screens. To get the
 * current display type of the modal, children can be passed as a function that
 * will be called with the current type.
 */
export default function Modal({
  open,
  children,
  showScrim = true,
  title,
  maxWidth = 540,
  type = "floating",
  backgroundColor = "primary",
  floatingAlignmentHorizontal = "center",
  floatingAlignmentVertical = "center",
  margin = 16,
  marginTop = margin,
  marginBottom = margin,
  marginLeft = margin,
  marginRight = margin,
  avoidKeyboard = false,
  onPressOutside,
  onPressCloseButton,
  onCloseComplete,
  portalHost = "middleground",
  insets: insetsProp,
}: {
  open: boolean
  children: ReactNode | ((type: ModalType) => ReactNode)
  showScrim?: boolean
  title?: string
  maxWidth?: number
  type?: ModalType
  backgroundColor?: BackgroundColor
  floatingAlignmentHorizontal?: AlignmentHorizontal
  floatingAlignmentVertical?: AlignmentVertical
  margin?: number
  marginTop?: number
  marginBottom?: number
  marginLeft?: number
  marginRight?: number
  avoidKeyboard?: boolean
  onPressOutside?: () => unknown
  onPressCloseButton?: () => unknown
  onCloseComplete?: () => unknown
  portalHost?: "foreground" | "middleground"
  insets?: { top?: number; bottom?: number; left?: number; right?: number }
}): React.JSX.Element | null {
  const manifest = useAppManifest()
  const { displayWidth, displayMode } = useTokens()
  const safeInsets = useSafeAreaInsets()
  const insets = { ...safeInsets, ...insetsProp }
  const [openAnimation, openState] = useOpenAnimation({
    open,
    onComplete: onCloseComplete
      ? (open) => !open && onCloseComplete()
      : undefined,
  })
  const modalType =
    displayWidth > maxWidth + marginRight + marginLeft ? "floating" : type
  const { t } = useTranslation()

  // Lock body scroll on the web when the scrim is shown over the page
  useLockBodyScroll(open && showScrim)

  // Blur the content behind the scrim on the web
  const backdropFilterStyle =
    Platform.OS === "web" && showScrim
      ? ({ backdropFilter: "blur(2px)" } as ViewStyle)
      : {}

  const transformAnimatedStyle = useAnimatedStyle(() => ({
    transform: [
      {
        translateY: interpolate(
          openAnimation.value,
          [0, 1],
          [MODAL_ANIMATION_VERTICAL_PX, 0],
        ),
      },
    ],
  }))

  const opacityAnimatedStyle = useAnimatedStyle(() => ({
    opacity: openAnimation.value,
  }))

  // Bail as early as possible
  if (openState === "closed") {
    // NOTE: Portal is always rendered regardless of openState to avoid issues
    // that can arise when Portal components are mounted and unmounted rapidly
    return <Portal hostName={portalHost}></Portal>
  }

  const modalContent = (
    <>
      {title || onPressCloseButton ? (
        // Header
        <Row
          paddingHorizontal="medium"
          backgroundColor="primary"
          position="relative"
          alignItems="center"
          justifyContent="flex-start"
          height={MODAL_HEADER_HEIGHT_PX}
          borderBottomColor={
            // If the modal background color is the same as the header background color
            // then we need a border separating the the modal body from the header
            backgroundColor === "primary" ? "shadowAdjacent" : undefined
          }
        >
          {title ? (
            <Title textStyle="headingSmall" level={4} numberOfLines={1}>
              {title}
            </Title>
          ) : null}
          {onPressCloseButton ? (
            <CloseButtonContainer>
              <IconButton
                id={kebabCase(`modal-close-btn${title ? `-${title}` : ""}`)}
                source={manifest.icons.close}
                iconSize="medium"
                onPress={onPressCloseButton}
                label={t("Close")}
              />
            </CloseButtonContainer>
          ) : null}
        </Row>
      ) : null}
      {typeof children === "function" ? children(modalType) : children}
      <SystemBars
        onPressBack={openState === "open" ? onPressCloseButton : undefined}
      />
    </>
  )

  switch (modalType) {
    case "fullscreen":
      return (
        <Portal hostName={portalHost}>
          <ModalTokensProvider displayMode={displayMode}>
            <KeyboardAvoidingContainer
              enabled={avoidKeyboard}
              behavior="height"
              pointerEvents="box-none"
            >
              <Container
                style={[opacityAnimatedStyle, transformAnimatedStyle]}
                $backgroundColor={backgroundColor}
                $touchable
              >
                {modalContent}
              </Container>
            </KeyboardAvoidingContainer>
          </ModalTokensProvider>
        </Portal>
      )
    case "floating":
      return (
        <Portal hostName={portalHost}>
          <ModalTokensProvider displayMode={displayMode}>
            <KeyboardAvoidingContainer
              enabled={avoidKeyboard}
              behavior="height"
              pointerEvents="box-none"
            >
              <Container style={[opacityAnimatedStyle, backdropFilterStyle]}>
                {showScrim || onPressOutside ? (
                  <Scrim $show={showScrim}>
                    {
                      // Listen for touches if onPressOutside is set and we're not in
                      // the process of closing. We pass touches through while closing
                      // to prevent the app from feeling unresponsive at the tail end
                      // of the close animation.
                      onPressOutside && openState !== "closing" ? (
                        <Touchable
                          id={kebabCase(
                            `modal-scrim-press-outside${title ? `-${title}` : ""}`,
                          )}
                          style={scrimTouchableStyle}
                          containerStyle={scrimTouchableStyle}
                          onPress={onPressOutside}
                        />
                      ) : null
                    }
                  </Scrim>
                ) : null}
                <Buffer
                  paddingTop={insets.top + marginTop}
                  paddingBottom={insets.bottom + marginBottom}
                  paddingLeft={insets.left + marginLeft}
                  paddingRight={insets.right + marginRight}
                  alignItems={flexAlignment[floatingAlignmentHorizontal]}
                  justifyContent={flexAlignment[floatingAlignmentVertical]}
                >
                  <BodyContainer
                    $backgroundColor={backgroundColor}
                    $maxWidth={maxWidth}
                    $type={modalType}
                    $openState={openState}
                    style={transformAnimatedStyle}
                    $showScrim={showScrim}
                  >
                    <Body
                      $type={modalType}
                      $showScrim={showScrim}
                      $paddingBottom={0}
                      $paddingLeft={0}
                      $paddingRight={0}
                      $backgroundColor={backgroundColor}
                    >
                      {modalContent}
                    </Body>
                  </BodyContainer>
                </Buffer>
              </Container>
            </KeyboardAvoidingContainer>
          </ModalTokensProvider>
        </Portal>
      )
    case "sheet":
      return (
        <Portal hostName={portalHost}>
          <ModalTokensProvider displayMode={displayMode}>
            <KeyboardAvoidingContainer
              enabled={avoidKeyboard}
              behavior="height"
              pointerEvents="box-none"
            >
              <Container style={[opacityAnimatedStyle, backdropFilterStyle]}>
                {showScrim || onPressOutside ? (
                  <Scrim $show={showScrim}>
                    {onPressOutside ? (
                      <Touchable
                        id={kebabCase(
                          `modal-scrim-press-outside${title ? `-${title}` : ""}`,
                        )}
                        style={scrimTouchableStyle}
                        containerStyle={scrimTouchableStyle}
                        onPress={onPressOutside}
                      />
                    ) : null}
                  </Scrim>
                ) : null}
                <Buffer
                  paddingTop={insets.top + marginTop}
                  paddingBottom={0}
                  paddingLeft={0}
                  paddingRight={0}
                  alignItems="center"
                  justifyContent="flex-end"
                >
                  <BodyContainer
                    $maxWidth={maxWidth}
                    $type={modalType}
                    $backgroundColor={backgroundColor}
                    $openState={openState}
                    style={transformAnimatedStyle}
                  >
                    <Body
                      $type={modalType}
                      $showScrim={showScrim}
                      $paddingBottom={insets.bottom}
                      $paddingLeft={insets.left}
                      $paddingRight={insets.right}
                      $backgroundColor={backgroundColor}
                    >
                      {modalContent}
                    </Body>
                  </BodyContainer>
                </Buffer>
              </Container>
            </KeyboardAvoidingContainer>
          </ModalTokensProvider>
        </Portal>
      )
    default: {
      modalType satisfies never
      return null
    }
  }
}

/**
 * Modals exist outside of context because of the Portal component, so this
 * component is necessary to make sure that the correct display mode bridges the
 * gap.
 */
function ModalTokensProvider({
  displayMode,
  children,
}: {
  displayMode: DisplayMode
  children: React.ReactNode
}) {
  const { displayMode: contextDisplayMode } = useTokens()
  return displayMode !== contextDisplayMode ? (
    <TokensProvider displayMode={displayMode}>{children}</TokensProvider>
  ) : (
    children
  )
}
